import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppInfo from "./AppInfo";
import { connectApp, openLink, getCurrentGoogleOwner } from "../api/AppLinkApi";
import { updateCurrentUserOnboardingSteps } from "../api/UserApi";
import RuleAction from "../actions/RuleAction";
import UserAction from "../actions/UserAction";
import LayoutAction from "../actions/LayoutAction";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import { CircleCheckBig, TriangleAlert, PackagePlus } from "lucide-react";
import { connectRedirectUrlAppIntegration } from "../Navigator";
import { useQuery } from "../AppLayout";
import { useTranslation } from "react-i18next";
import { getBaseUrl } from "../api/api";
import useUpgradePlan from "../team/hooks/useUpgradePlan";
import ConfirmDialog from "../dialog/ConfirmDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import AppSocial from "./components/AppSocial";
import McpConnectionsPanel from "./McpConnectionsPanel";

export const AppList = () => {
  const { t } = useTranslation();
  const { projectId }: any = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { invalidIntegration } = useUpgradePlan();
  const appUrl = getBaseUrl();

  const [appInfo, setAppInfo] = useState(undefined);
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });
  // null = not checked yet, true = valid, false = expired/invalid
  const [googleTokenValid, setGoogleTokenValid] = useState<boolean | null>(null);

  const isAdmin = useSelector((state: any) => state.userReducer.getIn(["currentUser", "admin"]));
  const appLinks = useSelector((state: any) => state.ruleReducer.get("appLinks"));
  const onboarding = useQuery().get("onboarding") || false;
  const appId = useQuery().get("appId") || "";

  useEffect(() => {
    dispatch(RuleAction.getAppLinks());
  }, [dispatch]);

  useEffect(() => {
    const googleApp = apps.find((a) => a.app === "google");
    if (googleApp?.connected) {
      getCurrentGoogleOwner()
        .then(() => setGoogleTokenValid(true))
        .catch(() => setGoogleTokenValid(false));
    }
  }, [appLinks]); // eslint-disable-line react-hooks/exhaustive-deps

  const apps = appLinks.toJS();

  useEffect(() => {
    document.title = t("Apps");
    if (appId != "") {
      const app = apps.find((item) => item.app == appId);
      if (app && app.hasSetting && checkRequireAdmin(app, isAdmin)) {
        setAppInfo(app);
        document.title = `${t("App")} ${app.app}`;
      } else {
        setAppInfo(undefined);
      }
    } else {
      setAppInfo(undefined);
    }
  }, [appId, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkRequireAdmin = (app, admin) => {
    return !app.requireAdmin || (app.requireAdmin && admin);
  };

  const statusActionLabel = (app) => {
    switch (app.app) {
      case "import_csv":
      case "zapier":
      case "mobile":
        return app.connected ? t("Disable") : t("Enable");
      default:
        return app.connected ? t("Disconnect") : t("Connect");
    }
  };

  const handleExploreAppOnboarding = () => {
    if (onboarding) {
      updateCurrentUserOnboardingSteps("explore-apps").then(() => {
        dispatch(UserAction.getCurrentUser());
      });
    }
  };

  const handleConnectApp = (app) => {
    if (invalidIntegration(app.app)) return;
    connectAppApi(app);
  };

  const handleDisconnectApp = (app) => {
    setShowConfirm({
      show: true,
      title: t("Disconnect App"),
      description: t("Are you sure you want to disconnect this app?"),
      type: "DISCONNECT_APP",
      data: app,
    });
  };

  const connectAppApi = (app, forceRedirect = false) => {
    const postUrl = connectRedirectUrlAppIntegration(projectId, app.app);
    connectApp(projectId, app.app, postUrl)
      .then((newAppData) => {
        const { redirectUrl = "" } = newAppData;
        if (redirectUrl != "" && (!app.connected || forceRedirect)) {
          openLink(redirectUrl);
        } else {
          dispatch(
            LayoutAction.setNotification({
              title: t("App"),
              type: "success",
              message: app.connected
                ? t("App disabled successfully!")
                : t("App enabled successfully!"),
            })
          );
          handleExploreAppOnboarding();
        }
      })
      .catch((error) => {
        dispatch(
          LayoutAction.setNotification({
            title: t("App"),
            type: "error",
            message:
              error?.response?.data?.message ||
              (app.connected ? t("App disable failed!") : t("App enable failed!")),
          })
        );
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(RuleAction.getAppLinks());
        }, 500);
      });
  };

  const handleSubmit = () => {
    if (showConfirm.type === "DISCONNECT_APP") {
      connectAppApi(showConfirm.data);
      closeApp();
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const openApp = (app) => {
    if (app.hasSetting) {
      navigate(`/workspaces/${projectId}/settings/apps?key=Apps&appId=${app.app}`);
    }
  };

  const closeApp = () => {
    navigate(`/workspaces/${projectId}/settings/apps?key=Apps`);
  };

  const renderSocialContentApp = () => {
    const socialApps = ["facebook", "instagram", "twitter", "linkedin", "threads", "canva"];
    return (
      <div className="grid grid-cols-12 gap-2 mt-2">
        {socialApps.map((socialApp) => {
          const app = appLinks.toJS().find((item: any) => item.app === socialApp);
          const isConnected = app?.connected || false;
          return (
            <div
              key={socialApp}
              className="col-span-12 lg:col-span-6 mb-2 cursor-pointer"
              onClick={() => openApp(app)}
            >
              <AppSocial
                appId={socialApp}
                connected={isConnected}
                onConnect={() => handleConnectApp(app)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderApps = () => {
    return (
      <div className="grid grid-cols-12 gap-4 mb-4">
        {apps
          .filter((app) => app.published && checkRequireAdmin(app, isAdmin))
          .map((app) => (
            <div
              key={app.name}
              className="col-span-12 sm:col-span-6 lg:col-span-4 rounded-md border border-gray-200 gap-6 p-6"
            >
              <div className="min-w-0 flex items-center justify-between">
                <div
                  className={`${app.app === "social_content_planner" ? "" : "cursor-pointer"}`}
                  onClick={() => openApp(app)}
                >
                  <img
                    className="w-8 h-8 object-contain flex-none"
                    src={`${appUrl}${app.imageUrl}`}
                    alt=""
                  />
                </div>
                {app.app === "social_content_planner" ? (
                  <div></div>
                ) : (
                  <div className="flex items-center">
                    {app.connectionError && (
                      <div className="flex items-center mr-4 connection-error-anchor-element">
                        <TriangleAlert className="w-5 h-5 text-red-400 mr-2" />
                        <Tooltip
                          anchorSelect=".connection-error-anchor-element"
                          content={t("Connection Error!")}
                        />
                        <button
                          onClick={() => handleConnectApp(app)}
                          type="button"
                          className="bg-red-400 flex items-center rounded-md px-3 py-2 text-xs font-semibold shadow-xs border-1 border-gray-300 border-solid hover:bg-blue-500 hover:text-white focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        >
                          <PackagePlus className="w-4 h-4 mr-2" />
                          {t("Re-connect")}
                        </button>
                      </div>
                    )}
                    {app.app === "google" && app.connected && googleTokenValid === false && (
                      <div className="flex items-center">
                        <Tooltip
                          anchorSelect=".google-token-expired-anchor"
                          content={t("Token expired — please reconnect.")}
                        />
                        <button
                          onClick={() => connectAppApi(app, true)}
                          type="button"
                          className="google-token-expired-anchor bg-yellow-400 text-yellow-900 flex items-center rounded-md px-3 py-2 text-xs font-semibold shadow-xs border-1 border-yellow-300 border-solid hover:bg-blue-500 hover:text-white focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        >
                          <TriangleAlert className="w-4 h-4 mr-2" />
                          {t("Re-connect")}
                        </button>
                      </div>
                    )}
                    {app.connected &&
                      !app.connectionError &&
                      !(app.app === "google" && googleTokenValid === false) && (
                        <div className="flex items-center mr-4 connected-anchor-element">
                          <CircleCheckBig className="w-5 h-5 text-green-400 mr-2" />
                          <Tooltip
                            anchorSelect=".connected-anchor-element"
                            content={t("Connected")}
                          />
                        </div>
                      )}
                    {!app.connected && !app.connectionError && (
                      <button
                        onClick={() => handleConnectApp(app)}
                        type="button"
                        className="flex items-center rounded-md px-3 py-2 text-xs font-semibold shadow-xs border-1 border-gray-300 border-solid hover:bg-blue-500 hover:text-white focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                      >
                        <PackagePlus className="w-4 h-4 mr-2" />
                        {statusActionLabel(app)}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div
                className={`min-w-0 ${app.app === "social_content_planner" ? "pt-6" : "py-6 cursor-pointer"}`}
                onClick={() => openApp(app)}
              >
                <div className="text-lg font-semibold leading-6 text-gray-900">{app.name}</div>
                <div className="mt-1 text-base leading-5 text-gray-500">{app.description}</div>
              </div>
              {app.app === "social_content_planner" && renderSocialContentApp()}
            </div>
          ))}
      </div>
    );
  };

  if (appInfo) {
    return (
      <div className={"w-full h-full p-4"}>
        <AppInfo
          appInfo={appInfo}
          onConnectOrDisconnect={(app) => {
            if (app.connected) {
              handleDisconnectApp(app);
            } else {
              handleConnectApp(app);
            }
          }}
          onClose={() => closeApp()}
          statusActionLabel={statusActionLabel}
        />
        <ConfirmDialog
          visible={showConfirm.show}
          title={showConfirm.title}
          description={showConfirm.description}
          alertIcon={<OctagonAlertIcon />}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowConfirm({ show: false, title: "", description: "" });
          }}
        />
      </div>
    );
  }

  return (
    <div className={"w-full h-full p-4"}>
      <div className="pb-4">
        <h1 className="text-lg font-semibold text-gray-900">{t("App Integrations")}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t("Connect Sales CRM to other tools that you use")}
        </p>
      </div>
      {renderApps()}
      <McpConnectionsPanel />
    </div>
  );
};

export default AppList;
